import { useEffect, useState } from "react";

import { AddPaymentMethodButton } from "features/payment-methods/components";
import usePaymentSources from "features/payment-methods/datahooks/usePaymentSources";
import type { PaymentSource } from "features/payment-methods/types";

import PoweredByStripe from "common/components/PoweredByStripe";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import ErrorMessage from "common/components/ui/ErrorMessage";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import PaymentSourceOption from "./PaymentSourceOption";

interface PaymentMethodSelectionProps {
  selectedPaymentSource: PaymentSource;
  addPaymentMethod: () => void;
  onBack: () => void;
  setPaymentSource: (paymentSource: PaymentSource) => void;
  isSettingPaymentSource: boolean;
  setPaymentSourceError: Error;
}

export default function PaymentMethodSelection({
  selectedPaymentSource,
  addPaymentMethod,
  onBack,
  setPaymentSource,
  isSettingPaymentSource,
  setPaymentSourceError,
}: PaymentMethodSelectionProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { paymentSources } = usePaymentSources();

  // Local state for tracking the currently selected payment method
  const [selectedMethod, setSelectedMethod] = useState(selectedPaymentSource);

  useEffect(() => {
    setSelectedMethod(selectedPaymentSource);
  }, [selectedPaymentSource]);
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <>
      <Header
        title="Select your preferred payment method"
        subtitle="Set a preferred payment method. You can change this at any time"
      />

      <Body className="gap-3">
        <div className="flex max-h-82 flex-col gap-y-3 overflow-y-auto scrollbar-thin">
          {/* Payment methods */}
          {paymentSources.map((paymentSource) => (
            <PaymentSourceOption
              key={paymentSource.id}
              paymentSource={paymentSource}
              isSelected={selectedMethod?.id === paymentSource.id}
              onSelect={() => setSelectedMethod(paymentSource)}
            />
          ))}
        </div>
        <AddPaymentMethodButton onClick={addPaymentMethod} />
        <PoweredByStripe />
        <ErrorMessage
          error={setPaymentSourceError}
          fallbackErrorMessage="Error setting payment source"
          size="sm"
        />
      </Body>

      <Footer className="items-center justify-end">
        <Button variant="secondary-black" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={() => setPaymentSource(selectedMethod)}
          isLoading={isSettingPaymentSource}
        >
          Confirm
        </Button>
      </Footer>
    </>
  );
}
